import React from 'react';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';
import { Card, CardContent } from './ui/card';
import { ShieldAlert } from 'lucide-react';

/**
 * PermissionGate - Controls access to features based on user permissions
 * 
 * Usage:
 * <PermissionGate permission="view_orders">
 *   <OrdersPage />
 * </PermissionGate>
 * 
 * Props:
 * - permission: string - The permission to check
 * - children: ReactNode - Content to render if permission granted
 * - fallback: ReactNode - Optional custom component to show if denied
 * - hideOnDenied: boolean - If true, returns null instead of access denied message
 */
const PermissionGate = ({ permission, children, fallback = null, hideOnDenied = false }) => {
  const { user } = useAuth();

  // If no permission specified or permission is 'all', allow access for authenticated users
  if (!permission || permission === 'all') {
    return <>{children}</>;
  }

  // Check permission using centralized permission system
  const userHasPermission = hasPermission(user, permission);

  if (!userHasPermission) {
    // If hideOnDenied is true, don't render anything (useful for navigation items)
    if (hideOnDenied) {
      return null;
    }

    // If custom fallback provided, use it
    if (fallback) {
      return <>{fallback}</>;
    }

    // Default permission denied message (for full page protection)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="mb-4">
              <div className="mx-auto h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <ShieldAlert className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this module.
            </p>
            <p className="text-sm text-gray-500">
              {user?.role === 'vendor' 
                ? 'This feature is only available to administrators.'
                : 'Contact your administrator if you need access to this feature.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default PermissionGate;
